import React, { useEffect, useMemo, useState } from 'react';
import { Layout, Typography, Dropdown, Row, Col } from '../../../components';
import { useLayoutContext } from '../../context/LayoutContext';
import { useLazyGetUserQuery, useLogoutMutation } from '../../../api';
import { useOrganization } from '../../sidebar/hooks/useOrganization';
import { DownOutlined, LogoutOutlined, TeamOutlined, UserOutlined, IdcardOutlined } from '@ant-design/icons';
import { ROUTES } from '../../../constants/routes';
import { ManageAccountModal } from './manageAccountModal';
import { SearchableOrgDropdown } from './SearchableOrgDropdown';
import './AppHeader.scss';

const { Header } = Layout;
const { Title, Text } = Typography;

const AppHeader = () => {
    const { activeTab } = useLayoutContext();
    const {
        organizations,
        currentOrganizationId,
        currentOrganizationName,
        changeOrganization,
    } = useOrganization({ setOpenKeys: () => {} });
    const [userEmail, setUserEmail] = useState('');
    const [openManageAccount, setOpenManageAccount] = useState(false);
    const [logout] = useLogoutMutation();
    const [getUser] = useLazyGetUserQuery();

    const userRole = sessionStorage.getItem('userRole');
    const systemRole = sessionStorage.getItem('systemRole');
    const userRoles = ['ADMIN', 'ORG_ADMIN', 'USER', 'VIEWER', 'SUPPORT'];

    useEffect(() => {
        const userId = sessionStorage.getItem('loggedInUserId');
        if (userId) {
            getUser({ id: userId })
                .unwrap()
                .then((userData) => {
                    setUserEmail(userData.email || '');
                })
                .catch((error) => {
                    console.error('Failed to fetch user data', error);
                });
        }
    }, [getUser]);

    const organizationMenuItems = {
        items: organizations.map((org) => {
            const isSystemAdminAccessible = org.name === 'RiverMeadow' && systemRole === 'ADMIN';
            const isOtherUserRoleAccessible = org.is_project && userRoles.includes(userRole);
            const isDisabled = !(isSystemAdminAccessible || isOtherUserRoleAccessible);

            return {
                key: org.id,
                disabled: isDisabled,
                label: <Text style={org.is_project ? { paddingLeft: 20 } : undefined}>{org.name}</Text>,
            };
        }),
        selectable: true,
        selectedKeys: currentOrganizationId ? [currentOrganizationId] : [],
        onClick: ({ key }) => changeOrganization(key),
    };

    const handleLogout = async () => {
        try {
            await logout();
            localStorage.clear();
            sessionStorage.clear();
            document.location.href = ROUTES.login;
        } catch (error) {
            document.location.href = '/?status=session_timeout';
        }
    };

    const userMenuItems = [
        {
            key: 'logout',
            icon: <LogoutOutlined style={{ fontSize: 16 }} />,
            label: 'Logout',
            onClick: handleLogout,
        },
        {
            key: 'manage_account',
            icon: <IdcardOutlined style={{ fontSize: 18 }} />,
            label: 'Manage Account',
            onClick: () => setOpenManageAccount(true),
        },
    ];

    const isSearchEnabledForOrgDropdown = useMemo(() => {
        const isSupportOrAdmin = ['ADMIN', 'SUPPORT'].includes(userRole) || ['ADMIN', 'SUPPORT'].includes(systemRole);
        const isSearchEnabled = WOWO.getConfigurationValue('enable_search_for_org_dropdown', localStorage);
        return isSupportOrAdmin || isSearchEnabled;
    }, [userRole, systemRole]);

    return (
        <Header className="app-header">
            <Row justify="space-between" align="middle" style={{ width: '100%' }}>
                <Col>
                    <Title level={2} className="page-title">
                        {activeTab || 'MOBILITY DASHBOARD'}
                    </Title>
                </Col>

                <Col>
                    <Row gutter={16} align="middle">
                        <Col>
                            {isSearchEnabledForOrgDropdown ? (
                                <SearchableOrgDropdown
                                    organizations={organizations}
                                    currentOrganizationId={currentOrganizationId}
                                    currentOrganizationName={currentOrganizationName}
                                    systemRole={systemRole}
                                    userRole={userRole}
                                    userRoles={userRoles}
                                    onSelect={changeOrganization}
                                />
                            ) : (
                                <Dropdown
                                    classNames={{ root: 'org-menu-dropdown' }}
                                    menu={organizationMenuItems}
                                    trigger={['click']}
                                >
                                    <div className="header-dropdown-item">
                                        <TeamOutlined className="header-icon" />
                                        <Text className="header-text">{currentOrganizationName}</Text>
                                        <DownOutlined className="header-arrow" />
                                    </div>
                                </Dropdown>
                            )}
                        </Col>

                        <Col>
                            <Dropdown
                                classNames={{ root: 'user-menu-dropdown' }}
                                menu={{ items: userMenuItems }}
                                trigger={['click']}
                                placement="bottomRight"
                            >
                                <div className="header-dropdown-item">
                                    <UserOutlined className="header-icon" />
                                    <Text className="header-text">{userEmail || 'User'}</Text>
                                    <DownOutlined className="header-arrow" />
                                </div>
                            </Dropdown>
                        </Col>
                    </Row>
                </Col>
            </Row>

            <ManageAccountModal open={openManageAccount} onCancel={() => setOpenManageAccount(false)} />
        </Header>
    );
};

export default AppHeader;
